import { useMemo, useState } from 'react'
import { Package, PackageMinus, PackagePlus } from 'lucide-react'
import { inventoryReasonLabels } from '../constants/inventory'
import { useInventoryStore } from '../store/inventoryStore'
import { TransactionDetails } from './TransactionDetails'
import type { InventoryReason } from '../constants/inventory'
import { Modal } from '@/components/custom/modal'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/utils/timeZoneUtils'
import { formatCurrency } from '@/utils/currencyUtils'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { useIsMobile, usePagination } from '@/hooks'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'

interface StockItemDetailsProps {
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
  itemId: string | null
}

export function StockItemDetails({
  trigger,
  open,
  onClose,
  itemId,
}: StockItemDetailsProps) {
  const isMobile = useIsMobile()
  const [transactionId, setTransactionId] = useState<string | null>(null)

  const inventoryStock = useInventoryStore((s) => s.inventoryStock)
  const transactions = useInventoryStore((s) => s.transactions)
  const categories = useInventoryStore((s) => s.categories)
  const churches = useChurchesStore((s) => s.churches)

  const stockItem = useMemo(
    () => inventoryStock.find((item) => item._id === itemId),
    [inventoryStock, itemId],
  )
  const itemCategory = useMemo(
    () => categories.find((cat) => cat._id === stockItem?.categoryId),
    [categories, stockItem],
  )
  const church = useMemo(
    () => churches.find((ch) => ch._id === stockItem?.churchId),
    [churches, stockItem],
  )
  const itemTransactions = useMemo(
    () =>
      transactions
        .filter((tx) => tx.inventoryStockId === itemId)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [transactions, itemId],
  )

  const { page, pageCount, rows, setPage } = usePagination({
    data: itemTransactions,
    pageSize: 5,
  })

  if (!stockItem) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        trigger={trigger}
        title="Stock Item Details"
        direction="right"
        contentClassName="min-w-[98vw]"
      >
        <div className="text-center py-8 text-gray-500">Item not found</div>
      </Modal>
    )
  }

  const getDisposalReasonLabel = (reason: InventoryReason) => {
    return inventoryReasonLabels[reason]
  }

  const handleTransactionClose = () => {
    setTransactionId(null)
  }

  const handleTransactionOpen = (id: string) => {
    setTransactionId(id)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      trigger={trigger}
      title="Stock Item Details"
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      <div className="space-y-6">
        {/* Item Overview */}
        <div className="bg-linear-to-r from-blue-50 to-blue-100 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Package className="h-8 w-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                {stockItem.item}
              </h2>
              {stockItem.brand && (
                <p className="text-sm text-gray-600 mt-1">{stockItem.brand}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-bold">Current Stock</p>
              <div className="flex justify-between items-baseline gap-2">
                <p className="text-3xl font-bold text-blue-600">
                  {stockItem.quantity}
                </p>
                <p className="text-sm text-gray-500">{stockItem.unit}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Item Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4 grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">
                Church
              </label>
              <p className="mt-1 text-base">{church?.name || 'N/A'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                Category
              </label>
              <p className="mt-1 text-base">{itemCategory?.name || 'N/A'}</p>
            </div>
          </div>

          <div className="space-y-4 grid grid-cols-2 gap-4">
            {stockItem.description && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Description
                </label>
                <p className="mt-1 text-base">{stockItem.description}</p>
              </div>
            )}
            {stockItem.location && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Location
                </label>
                <p className="mt-1 text-base">{stockItem.location}</p>
              </div>
            )}

            {stockItem.minThreshold !== undefined && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Minimum Threshold
                </label>
                <p className="mt-1 text-base">
                  {stockItem.minThreshold} {stockItem.unit}
                  {stockItem.quantity <= stockItem.minThreshold && (
                    <Badge variant="destructive" className="ml-2">
                      Low Stock
                    </Badge>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Transaction History */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Transaction History</h3>
          {itemTransactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              No transactions found
            </div>
          ) : isMobile ? (
            <ScrollArea className="h-[50vh]">
              <div className="space-y-3">
                {itemTransactions.map((tx) => (
                  <div
                    key={tx._id}
                    onClick={() => handleTransactionOpen(tx._id)}
                    className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        {tx.type === 'IN' ? (
                          <Badge
                            variant="outline"
                            className="inline-flex items-center gap-1 text-green-600 border-green-300 bg-green-50"
                          >
                            <PackagePlus className="h-3 w-3" />
                            In
                          </Badge>
                        ) : (
                          <Badge
                            variant="default"
                            className="inline-flex items-center gap-1 text-red-600 border-red-300 bg-red-50"
                          >
                            <PackageMinus className="h-3 w-3" />
                            Out
                          </Badge>
                        )}
                        <span className="text-sm text-gray-600">
                          {formatDate(tx.createdAt, 'MMM DD, YYYY')}
                        </span>
                      </div>
                      <span
                        className={`font-bold text-${tx.type === 'IN' ? 'green' : 'red'}-600`}
                      >
                        {tx.type === 'IN' ? '+' : '-'}
                        {tx.quantity} {tx.unit}
                      </span>
                    </div>
                    {tx.acquisition && (
                      <div className="space-y-1">
                        {tx.acquisition.price && (
                          <p className="font-medium text-green-600">
                            {formatCurrency(tx.acquisition.price)}
                          </p>
                        )}
                        {tx.acquisition.provider && (
                          <p className="text-sm text-gray-500">
                            Provider: {tx.acquisition.provider}
                          </p>
                        )}
                      </div>
                    )}
                    {tx.disposal && (
                      <p className="text-sm text-gray-600">
                        Reason: {getDisposalReasonLabel(tx.disposal.reason)}
                      </p>
                    )}
                    {tx.notes && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                        {tx.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <THead>
                    <TR>
                      <TH>Date</TH>
                      <TH>Type</TH>
                      <TH>Quantity</TH>
                      <TH>Details</TH>
                      <TH>Notes</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map((tx) => (
                      <TR
                        key={tx._id}
                        onClick={() => handleTransactionOpen(tx._id)}
                      >
                        <TD>{formatDate(tx.createdAt, 'MMM DD, YYYY')}</TD>
                        <TD>
                          {tx.type === 'IN' ? (
                            <Badge
                              variant="outline"
                              className="inline-flex items-center gap-1 text-green-600 border-green-300 bg-green-50"
                            >
                              <PackagePlus className="h-3 w-3" />
                              In
                            </Badge>
                          ) : (
                            <Badge
                              variant="default"
                              className="inline-flex items-center gap-1 text-red-600 border-red-300 bg-red-50"
                            >
                              <PackageMinus className="h-3 w-3" />
                              Out
                            </Badge>
                          )}
                        </TD>
                        <TD className="font-medium">
                          {tx.type === 'IN' ? '+' : '-'}
                          {tx.quantity} {tx.unit}
                        </TD>
                        <TD className="text-sm">
                          {tx.acquisition && (
                            <div>
                              {tx.acquisition.price && (
                                <p className="font-medium text-green-600">
                                  {formatCurrency(tx.acquisition.price)}
                                </p>
                              )}
                              {tx.acquisition.provider && (
                                <p className="text-gray-500">
                                  {tx.acquisition.provider}
                                </p>
                              )}
                            </div>
                          )}
                          {tx.disposal && (
                            <p className="text-gray-600">
                              {getDisposalReasonLabel(tx.disposal.reason)}
                            </p>
                          )}
                        </TD>
                        <TD className="text-sm text-gray-600 max-w-xs truncate">
                          {tx.notes || '-'}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
              <PaginationWrapper
                pageCount={pageCount}
                currentPage={page}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
        <TransactionDetails
          transactionId={transactionId}
          open={!!transactionId}
          onClose={handleTransactionClose}
        />
      </div>
    </Modal>
  )
}
